import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Button, Input, FormField } from '../ui';
import { PhoneInput } from '../ui/PhoneInput';
import { DNIInput } from '../ui/DNIInput';
import { useToast } from '../ui/toast';
import { type CountryCode } from '@leal-crm/shared/utils/phoneUtils';

interface CustomerFormData {
  name: string;
  email: string;
  phone: string;
  countryCode: CountryCode;
  address: string;
  dni: string;
}

interface Customer extends CustomerFormData {
  id: string;
}

interface CustomerFormProps {
  initialData?: Partial<CustomerFormData>;
  customerId?: string;
  onSuccess: (customer: Customer) => void;
  onCancel: () => void;
}

export function CustomerForm({
  initialData,
  customerId,
  onSuccess,
  onCancel,
}: CustomerFormProps) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    countryCode: (initialData?.countryCode as CountryCode) || 'ES',
    address: initialData?.address || '',
    dni: initialData?.dni || '',
  });
  const [errors, setErrors] = useState<Partial<CustomerFormData>>({});

  const isEditing = Boolean(customerId);

  const validateForm = (): boolean => {
    const newErrors: Partial<CustomerFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.invalidEmail');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('validation.required');
    } else if (!/^[+]?[\d\s-]{9,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = t('validation.invalidPhone');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name as keyof CustomerFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      let response: { success: boolean; data: Customer };

      if (isEditing) {
        response = await api.patch(`/api/customers/${customerId}`, formData);
      } else {
        response = await api.post('/api/customers', formData);
      }

      if (response.success) {
        addToast({
          variant: 'success',
          title: isEditing
            ? t('customer.updateSuccess')
            : t('customer.createSuccess'),
        });
        onSuccess(response.data);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      addToast({ variant: 'destructive', title: message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t('customer.name')}
          error={errors.name}
          required
        >
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Juan Garcia Lopez"
          />
        </FormField>

        <DNIInput
          value={formData.dni}
          onValueChange={(dni) => {
            setFormData((prev) => ({ ...prev, dni }));
            if (errors.dni) {
              setErrors((prev) => ({ ...prev, dni: undefined }));
            }
          }}
          label={t('customer.nif')}
          error={errors.dni}
          placeholder="12345678A"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t('customer.email')}
          error={errors.email}
          required
        >
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="cliente@email.com"
          />
        </FormField>

        <PhoneInput
          value={formData.phone}
          countryCode={formData.countryCode}
          onValueChange={(phone) => {
            setFormData((prev) => ({ ...prev, phone }));
            if (errors.phone) {
              setErrors((prev) => ({ ...prev, phone: undefined }));
            }
          }}
          onCountryChange={(countryCode) =>
            setFormData((prev) => ({ ...prev, countryCode }))
          }
          label={t('customer.phone')}
          error={errors.phone}
          required
          placeholder="612 345 678"
          autoDetectCountry={false}
        />
      </div>

      <FormField label={t('customer.address')}>
        <Input
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Calle Mayor 123, 28001 Madrid"
        />
      </FormField>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {isEditing ? t('common.save') : t('customer.create')}
        </Button>
      </div>
    </form>
  );
}
